// Dashboard del alumno: word-of-the-day + próxima clase + accesos rápidos.
// Para teacher/admin/inspector: solo accesos rápidos (sin widgets de alumno).

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Icon, type IconName } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { useAuth } from '@/context/AuthContext';
import { listMyBookings, type BookingListItem } from '@/services/bookings';
import { getWordOfTheDay, type VocabularyItem } from '@/services/vocabulary';
import styles from './Dashboard.module.scss';

interface QuickLink {
  to: string;
  label: string;
  icon: IconName;
  hint: string;
}

const STUDENT_LINKS: QuickLink[] = [
  { to: '/reservas',     label: 'Mis reservas', icon: 'calendar',       hint: 'Reserva o cancela tus clases' },
  { to: '/lecciones',    label: 'Lecciones',    icon: 'book',           hint: 'Repasa el material de tu nivel' },
  { to: '/grammar-bank', label: 'Grammar Bank', icon: 'book-open-text', hint: 'Recursos de gramática + ejercicios' },
  { to: '/aula',         label: 'Aula',         icon: 'video',          hint: 'Entra a clase en directo' },
  { to: '/mis-notas',    label: 'Mis notas',    icon: 'notebook-pen',   hint: 'Tus apuntes por clase' },
];

const TEACHER_LINKS: QuickLink[] = [
  { to: '/reservas',       label: 'Mis clases',        icon: 'calendar',       hint: 'Vista de tus clases del mes' },
  { to: '/disponibilidad', label: 'Mi disponibilidad', icon: 'clock',          hint: 'Patrones semanales y excepciones' },
  { to: '/grammar-bank',   label: 'Grammar Bank',      icon: 'book-open-text', hint: 'Revisa material de gramática' },
  { to: '/aula',           label: 'Aula',              icon: 'video',          hint: 'Entra a tus clases' },
  { to: '/informes',       label: 'Informes',          icon: 'bar-chart',      hint: 'Asistencia y progreso' },
];

const ADMIN_LINKS: QuickLink[] = [
  { to: '/admin/usuarios',   label: 'Usuarios',     icon: 'users',          hint: 'Alumnos, profes y permisos' },
  { to: '/grammar-bank',     label: 'Grammar Bank', icon: 'book-open-text', hint: 'Gestión de fichas de gramática' },
  { to: '/admin/vocabulary', label: 'Vocabulary',   icon: 'book',           hint: 'Catálogo de palabras' },
  { to: '/informes',         label: 'Informes',     icon: 'bar-chart',      hint: 'Reporting global' },
  { to: '/admin/emails',     label: 'Emails',       icon: 'mail',           hint: 'Auditoría de envíos' },
];

function pickLinks(roles: string[]): QuickLink[] {
  if (roles.includes('admin')) return ADMIN_LINKS;
  if (roles.includes('teacher')) return TEACHER_LINKS;
  return STUDENT_LINKS;
}

function formatNextClass(b: BookingListItem): string {
  const d = new Date(b.scheduled_at);
  const day = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  const time = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return `${day.charAt(0).toUpperCase() + day.slice(1)} · ${time}`;
}

export function Dashboard() {
  const { state } = useAuth();
  const roles = state.status === 'authenticated' ? state.user.roles : [];
  const userName = state.status === 'authenticated' ? state.user.email.split('@')[0] : '';
  const isStudent = roles.includes('student');

  const [wod, setWod] = useState<VocabularyItem | null>(null);
  const [wodLoading, setWodLoading] = useState(false);
  const [nextClass, setNextClass] = useState<BookingListItem | null>(null);
  const [nextLoading, setNextLoading] = useState(false);

  useEffect(() => {
    if (!isStudent) return;
    let cancelled = false;
    setWodLoading(true);
    getWordOfTheDay()
      .then((it) => { if (!cancelled) setWod(it); })
      .catch(() => { /* ignore */ })
      .finally(() => { if (!cancelled) setWodLoading(false); });
    return () => { cancelled = true; };
  }, [isStudent]);

  useEffect(() => {
    if (!isStudent) return;
    let cancelled = false;
    setNextLoading(true);
    const now = new Date();
    listMyBookings(now.getFullYear(), now.getMonth() + 1)
      .then((r) => {
        if (cancelled) return;
        const upcoming = r.items
          .filter((b) => b.status === 'scheduled' && new Date(b.scheduled_at).getTime() > Date.now())
          .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
        setNextClass(upcoming[0] ?? null);
      })
      .catch(() => { /* ignore */ })
      .finally(() => { if (!cancelled) setNextLoading(false); });
    return () => { cancelled = true; };
  }, [isStudent]);

  const links = useMemo(() => pickLinks(roles), [roles]);

  return (
    <>
      <Header title="Inicio" />
      <main className={styles.main}>
        <p className={styles.greeting}>
          {userName ? `¡Hola, ${userName}!` : '¡Hola!'} {isStudent ? 'Aquí tienes tu resumen de hoy.' : 'Bienvenido a Knooowy.'}
        </p>

        {isStudent && (
          <div className={styles.widgets}>
            <section className={styles.widget}>
              <div className={styles.widgetHeader}>
                <Icon name="book" size={18} />
                <h3>Word of the day</h3>
              </div>
              {wodLoading ? (
                <div className={styles.center}><Spinner size="sm" /></div>
              ) : !wod ? (
                <p className={styles.muted}>Aún no hay palabra para tu nivel. Pídeselo al admin.</p>
              ) : (
                <div className={styles.wodBody}>
                  {wod.image_url && <img src={wod.image_url} alt={wod.word} className={styles.wodImage} />}
                  <div className={styles.wodText}>
                    <h2 className={styles.wodWord}>{wod.word}</h2>
                    {wod.pronunciation && <p className={styles.wodPron}>{wod.pronunciation}</p>}
                    {wod.translation && <p className={styles.wodTrans}>{wod.translation}</p>}
                    {wod.level_label && <Badge tone="accent">{wod.level_label}</Badge>}
                  </div>
                </div>
              )}
            </section>

            <section className={styles.widget}>
              <div className={styles.widgetHeader}>
                <Icon name="calendar" size={18} />
                <h3>Próxima clase</h3>
              </div>
              {nextLoading ? (
                <div className={styles.center}><Spinner size="sm" /></div>
              ) : !nextClass ? (
                <p className={styles.muted}>No tienes clases programadas próximamente.</p>
              ) : (
                <div className={styles.nextBody}>
                  <p className={styles.nextWhen}>{formatNextClass(nextClass)}</p>
                  <p className={styles.nextWith}>Con {nextClass.teacher_name}</p>
                  <Badge tone={nextClass.type === 'gr' ? 'accent' : 'neutral'}>
                    {nextClass.type === 'gr' ? 'Grupal' : 'Individual'}
                  </Badge>
                </div>
              )}
            </section>
          </div>
        )}

        <section className={styles.quickAccess}>
          <h3 className={styles.sectionTitle}>Accesos rápidos</h3>
          <ul className={styles.linksGrid}>
            {links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className={styles.linkCard}>
                  <span className={styles.linkIcon}><Icon name={l.icon} size={20} /></span>
                  <span className={styles.linkBody}>
                    <span className={styles.linkLabel}>{l.label}</span>
                    <span className={styles.linkHint}>{l.hint}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </>
  );
}
